import { CanActivateFn } from '@angular/router';

import { environment } from '@config/environments/environment';

/**
 * Chuyển hướng sang **cổng sinh viên mới** (Student Workspace, Next.js).
 *
 * ── Vì sao chặn ở ROUTE chứ không sửa từng nơi gọi ──────────────────────────
 * Riêng trang đăng nhập, app này có **14 chỗ** điều hướng tới `login`
 * (`AppRoutingService`, 6 màn xác thực lần đầu, quên mật khẩu, đăng xuất,
 * `login-by-sso`, `guest-layout.routing`…). Sửa từng chỗ là chắc chắn sót một
 * chỗ, và mã viết sau lại sót tiếp. Chặn ở route thì mọi lối đều đi qua đây.
 *
 * ⚠ Chỉ bắt được điều hướng qua **router**. Hai nơi dùng thẳng
 * `window.location.href` (`AccessTokenGuard` và `api-version-1-response.interceptor`,
 * qua khoá `useCustomLoginPageURL`) KHÔNG chạm router — phải khai riêng ở
 * `environment.*.ts`.
 *
 * ── Địa chỉ đích theo môi trường (`environment.domain.urlCongSvMoi`) ─────────
 *
 * | Môi trường | Gốc |
 * |---|---|
 * | dev | `http://localhost:4001` |
 * | test · beta | `https://sinhvien.ttithutech.com` |
 * | prod | `https://sinhvien.hutech.edu.vn` |
 *
 * ⚠ **KHÔNG dùng `environment.domain.urlWebLogIn`** cho việc này. Khoá đó kiêm
 * luôn `backlink` của SSO (`authen.config.ts` §sso: `ssoLoginPath`,
 * `ssoRedirectPath`) — đổi nó là gãy luồng đăng nhập SSO của chính app này.
 */

/** Dựng địa chỉ đầy đủ trên cổng mới. Trả chuỗi rỗng nếu môi trường chưa khai. */
export function duongDanCongSvMoi(duongDan: string): string {
  const goc = environment.domain.urlCongSvMoi;
  if (!goc) return '';
  return `${goc.replace(/\/+$/, '')}${duongDan}`;
}

/**
 * Dựng guard chuyển hướng cho một đường dẫn trên cổng mới.
 *
 * Ba quyết định, đều có lý do:
 *
 * **1 · Đặt TRƯỚC guard xác thực.** Guard chạy theo thứ tự khai và dừng khi một
 * cái trả `false`. Đứng trước thì người chưa đăng nhập cũng được đẩy thẳng sang —
 * bắt họ đăng nhập vào app cũ chỉ để bật ra ngoài là vô nghĩa.
 *
 * **2 · KHÔNG kèm token.** Cổng mới tự lo phiên của nó (luồng SSO OAuth2); chưa
 * đăng nhập thì nó tự đưa sang SSO. Giống hệt cách app Angular 5 vẫn làm.
 *
 * **3 · Thiếu cấu hình thì ĐI TIẾP, không chặn.** `urlCongSvMoi` rỗng nghĩa là
 * môi trường đó chưa khai (`environment.ts` gốc để rỗng) — lúc đó mở màn nội bộ
 * còn hơn ném người dùng vào trang trắng.
 */
function taoGuard(duongDan: string): CanActivateFn {
  return (route) => {
    const dich = duongDanCongSvMoi(duongDan);
    if (!dich) return true;

    /**
     * Cửa sau để còn ĐỐI CHIẾU hai giao diện: thêm `?noi-bo=1` là mở bản trong
     * app này. Giữ vì S02 xác định v4 vừa là đường lùi vừa là bản đối chiếu cho
     * tới hết đợt C.
     */
    if (route.queryParamMap.get('noi-bo') === '1') return true;

    // `replace` chứ không `assign`: bấm Quay lại sẽ về trang trước đó, không rơi
    // lại vào chính địa chỉ vừa bị chuyển tiếp rồi nảy đi tiếp.
    window.location.replace(dich);
    return false;
  };
}

/** `/workspace` → Student Workspace của cổng mới. */
export const redirectCongSvMoiGuard: CanActivateFn = taoGuard('/workspace');

/** `/login` → trang đăng nhập của cổng mới. */
export const redirectDangNhapCongSvMoiGuard: CanActivateFn = taoGuard('/login');
